// src/pages/hr/HRReports.jsx
import React, { useState, useEffect } from "react";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/config";

const PANEL_LABEL = "HR Panel";

function MetricCard({ label, value, icon, color }) {
  return (
    <div style={{ background: "var(--bg-card, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 10, padding: "1.1rem 1rem", display: "flex", alignItems: "center", gap: "0.85rem", borderTop: `3px solid ${color}`, boxShadow: "0 1px 5px rgba(0,0,0,0.05)" }}>
      <span style={{ fontSize: "1.7rem", color }}>{icon}</span>
      <div>
        <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--text-primary, #111)", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

export default function HRReports() {
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    return onSnapshot(collection(db, "users"), (s) => {
      setEmployees(s.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const active = employees.filter((e) => e.isActive !== false);
  const deptGroups = {};
  employees.forEach((e) => {
    const d = e.department || "Unassigned";
    deptGroups[d] = (deptGroups[d] || 0) + 1;
  });
  const deptEntries = Object.entries(deptGroups).sort((a, b) => b[1] - a[1]);

  return (
    <Layout navItems={HR_NAV} panelLabel={PANEL_LABEL}>
      <style>{`
        .reports-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 1.75rem; }
        .reports-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem 1.4rem; box-shadow: 0 1px 5px rgba(0,0,0,0.06); }
        .reports-card-title { font-size: 0.92rem; font-weight: 700; color: var(--text-primary, #111); margin: 0 0 1rem; }
        .dept-bar-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; font-size: 0.83rem; }
        .dept-bar-label { min-width: 100px; color: var(--text-primary, #111); font-weight: 500; }
        .dept-bar-wrap { flex: 1; height: 10px; background: #e5e7eb; border-radius: 99px; overflow: hidden; }
        .dept-bar-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, #1b4b31, #3da068); }
        .dept-bar-count { min-width: 28px; text-align: right; font-weight: 700; color: #1b4b31; }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Organization-wide HR metrics and workforce analytics</p>
        </div>
        <button className="btn-secondary">📥 Export CSV</button>
      </div>

      <div className="reports-grid">
        <MetricCard label="Total Employees" value={employees.length} icon="👥" color="#1b4b31" />
        <MetricCard label="Active" value={active.length} icon="✅" color="#10b981" />
        <MetricCard label="Inactive" value={employees.length - active.length} icon="❌" color="#ef4444" />
        <MetricCard label="Team Leads" value={employees.filter(e => e.role === "lead").length} icon="👑" color="#6366f1" />
        <MetricCard label="HR Executives" value={employees.filter(e => e.role === "hr").length} icon="🧑‍💼" color="#8b5cf6" />
        <MetricCard label="Admins" value={employees.filter(e => e.role === "admin").length} icon="🔑" color="#f59e0b" />
        <MetricCard label="Full-Time" value={employees.filter(e => e.employmentType === "full-time").length} icon="💼" color="#0ea5e9" />
        <MetricCard label="Interns" value={employees.filter(e => e.employmentType?.includes("intern")).length} icon="🎓" color="#ec4899" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
        {/* Department Distribution */}
        <div className="reports-card">
          <div className="reports-card-title">🏢 Headcount by Department</div>
          {deptEntries.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No data available</p>
          ) : deptEntries.map(([dept, count]) => (
            <div className="dept-bar-row" key={dept}>
              <div className="dept-bar-label">{dept}</div>
              <div className="dept-bar-wrap">
                <div className="dept-bar-fill" style={{ width: `${(count / Math.max(...deptEntries.map(([,c])=>c))) * 100}%` }} />
              </div>
              <div className="dept-bar-count">{count}</div>
            </div>
          ))}
        </div>

        {/* Role Distribution */}
        <div className="reports-card">
          <div className="reports-card-title">👤 Role Distribution</div>
          {[
            { label: "Employees", count: employees.filter(e => e.role === "employee").length, color: "#10b981" },
            { label: "Team Leads", count: employees.filter(e => e.role === "lead").length, color: "#6366f1" },
            { label: "HR", count: employees.filter(e => e.role === "hr").length, color: "#8b5cf6" },
            { label: "Admin", count: employees.filter(e => e.role === "admin").length, color: "#f59e0b" },
          ].map(({ label, count, color }) => {
            const pct = employees.length > 0 ? Math.round((count / employees.length) * 100) : 0;
            return (
              <div className="dept-bar-row" key={label}>
                <div className="dept-bar-label">{label}</div>
                <div className="dept-bar-wrap">
                  <div className="dept-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}aa)` }} />
                </div>
                <div className="dept-bar-count" style={{ color }}>{count}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
